<template>
    <div class="phonenumberinput formcontrol" :class="containerClasses">
        <label for="phoneNumber">{{ label }}</label>

        <input
            type="tel"
            name="phoneNumber"
            value=""
            @keyup="updateResult"
            @focus="onInputFocus"
            @blur="onInputBlur"
            @countrychange="updateResult"
            :disabled="props.disabled"
            ref="phoneNumberElement"
        />
    </div>
</template>

<script setup lang="ts">
    import { trans } from "@/app/localization/Trans";
    import { InputRequirementType } from "@/components/elements/InputRequirementType";
    import { Country } from "@/data/model/Enums";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { translations } from "@/shared/localization/translations";
    import intlTelInput from "intl-tel-input";
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

    const tableAllocation = useTableAllocationStore();

    type PhoneNumberResult = {
        phoneNumber: string | undefined;
        dialCode: string | undefined;
        countryCode: string | undefined;
        isValid: boolean;
    };

    // Props
    const props = defineProps<{
        indent?: boolean;
        disabled?: boolean;
        label?: string;
        value?: string;
        requirementType?: InputRequirementType;
    }>();

    const emit = defineEmits(["onStartEditing", "onEndEditing"]);

    const result = ref<PhoneNumberResult>({
        phoneNumber: undefined,
        dialCode: undefined,
        countryCode: undefined,
        isValid: true,
    });

    let plugin: intlTelInput.Plugin | undefined = undefined;
    onMounted(() => {
        plugin = intlTelInput(phoneNumberElement.value!, {
            preferredCountries: ["ch", "de", "fr", "it", "at", "nl"],
            initialCountry: getInitialCountry(),
            allowDropdown: true,
            autoPlaceholder: "aggressive",
            nationalMode: true,
            separateDialCode: true,
            utilsScript: "./assets/scripts/libs/intl-tel-input-utils.js",
        });

        plugin.setNumber(props.value ?? "");
        updateResult();
    });

    onBeforeUnmount(() => {
        plugin?.destroy();
    });

    // State
    const isEditing = ref(false);
    const isPhoneNumberValid = ref<boolean | undefined>(undefined);
    const showError = ref(false);
    const phoneNumberElement = ref<HTMLElement | undefined>();

    // Computed
    const containerClasses = computed(() => ({
        intent: props.indent,
        editing: isEditing.value,
        "has-error": showError.value,
        disabled: props.disabled,
    }));

    const label = computed<string | undefined>(() => {
        const text = props.label ?? trans.signup.phoneNumber;

        if (props.requirementType === InputRequirementType.Optional) {
            return text + " (" + translations.addToCartPopover.optional + ")";
        }

        return text;
    });

    // Methods
    function updateResult() {
        checkPhoneNumberInput();

        if (isPhoneNumberValid.value) {
            result.value.phoneNumber = getNumber();
            result.value.dialCode = getDialCode();
            result.value.countryCode = getCountryCode();
            result.value.isValid = isValid();
            return;
        }

        result.value.phoneNumber = undefined;
        result.value.dialCode = undefined;
        result.value.isValid = false;
    }

    function getNumber(): string | undefined {
        const nr = plugin?.getNumber();
        if (nr == null) return undefined;

        return nr;
    }

    function getDialCode(): string | undefined {
        const ext = plugin?.getSelectedCountryData();
        if (ext == null) return undefined;

        return ext.dialCode;
    }

    function getCountryCode(): string | undefined {
        const ext = plugin?.getSelectedCountryData();
        if (ext == null) return undefined;

        return ext.iso2;
    }

    function isValid(): boolean {
        if (plugin === undefined) return true;

        if (
            props.requirementType === InputRequirementType.Optional ||
            props.requirementType === InputRequirementType.NotRequired
        ) {
            if (plugin.getNumber() == undefined || plugin.getNumber().length == 0) {
                console.log("Phone number is not provided");
                return true;
            }
        }

        return plugin.isValidNumber();
    }

    function checkPhoneNumberInput() {
        isPhoneNumberValid.value = isValid();

        if (isPhoneNumberValid.value) {
            showError.value = false;
        }
    }

    function getInitialCountry() {
        if (tableAllocation.allocation) {
            switch (tableAllocation.allocation.country) {
                case Country.Germany:
                    return "de";
                case Country.Italy:
                    return "it";
                case Country.France:
                    return "fr";
                case Country.Switzerland:
                    return "ch";
                case Country.Austria:
                    return "at";
                case Country.Netherlands:
                    return "nl";
            }
        }

        return "ch";
    }

    // Exposed API
    function setValue(phoneNumber: string | undefined) {
        plugin?.setNumber(phoneNumber === undefined ? "" : phoneNumber);
        updateResult();
    }

    function setError(error: boolean) {
        showError.value = error;
    }

    function focus() {
        if (phoneNumberElement.value) {
            $(phoneNumberElement.value).focus();
        }
    }

    function getResult() {
        console.log("Result", result.value.dialCode);
        return result.value;
    }

    // Event Handlers
    function onInputFocus() {
        isEditing.value = true;
    }

    function onInputBlur() {
        isEditing.value = false;
    }

    // Watchers
    watch(
        () => props.value,
        (newValue) => setValue(newValue ?? "")
    );


    watch(isEditing, (newValue) => {
        if (newValue)
            emit("onStartEditing");
        else
            emit("onEndEditing");
    });


    // Exposed API
    defineExpose({
        isValid,
        setValue,
        getResult,
        setError,
        focus,
    });
</script>

<style lang="scss" scoped>
    input::placeholder {
        color: #aaaaaa;
        opacity: 1;
    }
</style>